import newMsgImg from '@/features/notifications/images/new-mark.png';
import Image, { StaticImageData } from 'next/image';
import markedImg from '@/features/notifications/images/check-mark.png';
import React, { useRef, useState, useEffect } from 'react';
import { useTranslations } from "next-intl";
import { NotificationItem } from "@/features/notifications/services";
import { track } from "@/utils/track";
import { NotificationEventType, TRACK_MSG_TYPES, TRACK_MSG_TYPE_VALUE } from '../constant';
import IconFont from '@/components/ui/iconFont';


interface MsgCardProps {
  // 消息ID
  msgId: string;
  icon?: StaticImageData;
  title: string;
  content: string;
  time: string;
  coverUrl?: string;
  isRead: boolean;
  // 消息对象信息,有时间要优化这个的传入，正常来讲不需要传入此对象，重构时间紧迫先保留传入
  msgDetail: NotificationItem;
  // 底部左侧操作按钮插槽
  btnGroupSlot?: React.ReactNode,

  className?: string,
  titleClassName?: string,

  onReadClick?: (msgDetail: NotificationItem) => void;
  onExpandClick?: (msgDetail: NotificationItem, isShowAll: boolean) => void;
}

export default function MsgCard({ icon, title, content, time, coverUrl, isRead = false, msgDetail, btnGroupSlot, className, titleClassName, onReadClick, onExpandClick }: MsgCardProps) {
  const t = useTranslations('notifications'); // 多语言模块  

  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const contentRef = useRef<HTMLDivElement>(null);

  const [maxHeight, setMaxHeight] = useState<number>(45);

  // 是否显示展开收起按钮（内容超过两行时才显示）
  const [isExpanded, setIsExpanded] = useState(false);
  // 展开收起状态: 0=收起 1=展开（0时显示展开按钮， 1时显示收起按钮）
  const [expandStatus, setExpandStatus] = useState<0 | 1>(0);


  useEffect(() => {
    if (contentRef.current) {
      // 内容元素的高度(目前设置的行高为15px，所以超过两行时高度为30px)
      const contentHeight = contentRef.current.offsetHeight;
      if (contentHeight > 30) {
        setIsExpanded(true);
      }
    }
  }, [contentRef]);

  const handleExpandClick = (e: React.MouseEvent) => {

    e.stopPropagation();

    if (expandStatus === 0) {
      // 点击showall按钮
      const wrapper = contentWrapperRef.current;
      if (wrapper) {
        const realHeight = wrapper.scrollHeight;
        setMaxHeight(realHeight);
      }
      onExpandClick?.(msgDetail, true);
      handleExpandClickTrack('Show All');
    } else {
      // 点击hide按钮
      setMaxHeight(45);
      onExpandClick?.(msgDetail, false);
      handleExpandClickTrack('Hide');
    }

    setExpandStatus(expandStatus === 0 ? 1 : 0);
  };

  // 点击展开收起数据上报
  function handleExpandClickTrack(btnName: 'Show All' | 'Hide') {
    const { event_type, id, msg_config_id } = msgDetail || {} as NotificationItem;
    if (TRACK_MSG_TYPES.includes(event_type)) {
      const trackParams: Record<string, string> = {
        message_type: TRACK_MSG_TYPE_VALUE[event_type],
        message_push_id: id,
        display_format: 'Notification Center',
        button_name: btnName
      };
      if (event_type === NotificationEventType.Platform) {
        trackParams.message_id = msg_config_id;
      }

      // 上报埋点
      track('inbox_click', trackParams);
    }
  }

  const handleReadClick = (e: React.MouseEvent) => {
    console.log('handleReadClick');
    e.stopPropagation();
    onReadClick?.(msgDetail);
  };

  return (
    <div className={`w-full bg-[#222938] box-border rounded-[8px] px-[15px] py-[25px] pb-[15px] relative ${className || ''}`}>
      {/* 新消息标记 */}
      {!isRead && (
        <Image src={newMsgImg} alt="" className="w-[34px] h-[17px] absolute top-0 right-0" />
      )}

      <div className="box-border pb-[12px] border-b-[1px] border-[rgba(214,234,255,0.06)] flex justify-between items-start relative">
        {/* 消息图标 */}
        {
          icon && (
            <Image src={icon} alt="" className="w-[35px] object-contain h-auto mr-[10px]" />
          )
        }

        {/* 消息内容 */}
        <div className="flex-1 mt-[-4px]">
          <div className={`text-[13px] font-semibold w-full break-words ${titleClassName || ''}`}>
            {title}
          </div>
          <div className="text-[12px] text-tertiary mt-[15px] mb-[15px] font-medium leading-[0px]">
            {time}
          </div>

          {/* 封面图（平台消息用到） */}
          {coverUrl && (
            <Image src={coverUrl} alt="" width={336} height={93} className="object-contain w-full h-auto mb-[10px]" />
          )}

          <div ref={contentWrapperRef} className="flex-1 transition-[max-height] duration-300 overflow-hidden" style={{ maxHeight: maxHeight + 'px' }}>
            <div
              ref={contentRef}
              className={`w-full text-[12px] text-tertiary leading-[15px] font-medium overflow-hidden`}
              style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-line' }}
            >
              {content}
            </div>
          </div>
          {/* 占位元素 */}
          {isExpanded && (
            <div
              className="w-full transition-[height] duration-300"
              style={{ height: expandStatus === 1 ? '30px' : '5px' }}
            />
          )}
        </div>

        {/* 展开/收起按钮 */}
        {
          isExpanded && (
            <>
              {/* 展开状态 */}
              {expandStatus === 0 && (
                <div className="absolute bottom-[0px] left-0 right-0 h-[35px] box-border pb-[18px] flex justify-center items-end bg-[linear-gradient(to_top,#222938_0%,rgba(34,41,56,0.95)_73%,transparent_100%)]">
                  <div
                    className="flex items-center text-[12px] text-white font-medium cursor-pointer leading-[9px] pressable active:scale-90"
                    onClick={handleExpandClick}
                  >
                    {t('showAll')}
                    <IconFont name="arrow-down" size={7} color="#FFFFFF" className="ml-[5px]" />
                  </div>
                </div>
              )}

              {/* 收起状态 */}
              {expandStatus === 1 && (
                <div className="absolute bottom-[0px] left-0 right-0 flex justify-center items-end pb-[15px]">
                  <div
                    className="flex justify-center items-center text-[12px] text-white font-medium cursor-pointer leading-[9px] pressable"
                    onClick={handleExpandClick}
                  >
                    {t('hide')}
                    <IconFont name="arrow-up" size={7} color="#FFFFFF" className="ml-[5px]" />
                  </div>
                </div>
              )}
            </>
          )
        }
      </div >

      {/* 底部按钮 */}
      < div className="w-full flex items-center justify-between mt-[15px]" >
        {/* 左侧跳转按钮 */}
        < div className='flex items-center' >
          {btnGroupSlot}
        </div >

        {/* 已读按钮 */}
        {
          !isRead && (
            <div className="flex items-center text-secondary text-[12px] pressable" onClick={handleReadClick}>
              <div>{t('markAsRead')}</div>
              <Image src={markedImg} alt="" className="w-[14px] h-[13px] ml-[10px]" />
            </div>
          )
        }
      </div>
    </div>
  );
}